import React, { useState, useEffect, useCallback, memo, Fragment } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import ReactTable from "react-table";
import withFixedColumns from "react-table-hoc-fixed-columns";
import ClipboardJS from "clipboard";
import moment from "moment";
import ls from "localstorage-slim";
import axios from "../../../../../axios-instance";
import { useShowActionsMenu } from "../../../../../components/UI/ActionsMenu/hooks/useShowActionsMenu";
import { useLazyLoadOnTableScroll } from "../../../../../hooks/useLazyLoadOnTableScroll";
import { ApproveTransactionModal } from "./Modals/ApproveTransactionModal";
import { EditTransactionModal } from "./Modals/EditTransactionModal";
import { RefundTicketsModal } from "./Modals/RefundTicketsModal";
import { getHeaders } from "../../../../../helpers/getHeaders";
import { loadFile } from "../../../../../helpers/loadFile";
import Icon from "../../../../../components/UI/Icon/Icon";
import { StatusLabel } from "../../../../../components/Transactions/StatusLabel/StatusLabel";
import { TransactionActions } from "./TransactionActions.js";
import { paymentStatuses } from "../../../../../constants/paymentStatuses";
import CheckBox from "../../../../../components/UI/Checkbox/CheckBox";
import NewLoader from "../../../../../components/NewLoader/NewLoader";
import PageTitle from "../../../../../components/UI/PageTitle/PageTitle";
import { approveTransaction, cancelOfferTransaction, clearTransactions, deleteSeatsTransaction, editTransaction, getTransactions } from "../../../../../store/actions/TransactionsActions";
import { getEventSellStats } from "../../../../../store/actions/eventsActions";
import { getPaymentTypeLabel, paymentTypes, paymentOptions } from "../../../../../constants/paymentType.ts";

import { sortTypes } from "../../../../../constants/transactions.ts";
import debounce from "lodash.debounce";
import { getCustomURLs } from "../../../../../helpers/getCustomURLs.js";
import PleaseWaitModal from "../../../../BarCodes/modals/PleaseWaitModal.js";
import { TabelSettings } from "../../../../../components/TabelSettings/TabelSettings.jsx";

import "react-table-hoc-fixed-columns/lib/styles.css";
import "react-table/react-table.css";
import "./EventTransactions.sass";

const MySwal = withReactContent(Swal);

function arePropsEqual(oldProps, newProps) {
  return (
    oldProps.transactionsList === newProps.transactionsList &&
    oldProps.pageSize === newProps.pageSize &&
    oldProps.currentPageNumber === newProps.currentPageNumber &&
    oldProps.columns === newProps.columns
  );
}

const Table = memo(({
  columns,
  currentPageNumber,
  pageSize,
  transactionsList,
  handleCurrentPageChanged,
  handlePageSizeChanged,
  handleTransactionsTableScroll,
  totalTransactions
}) => {
  const ReactTableFixedColumns = withFixedColumns(ReactTable)

  return (
    <ReactTableFixedColumns
      data={transactionsList}
      columns={columns}
      manual
      showPagination={true}
      pageSizeOptions={[100, 200, 500, 1000, 100000]}
      minRows={0}
      page={currentPageNumber}
      pageSize={pageSize}
      pages={Math.ceil(totalTransactions / pageSize)}
      onPageChange={handleCurrentPageChanged}
      onPageSizeChange={handlePageSizeChanged}
      sortable={false}
      getTrProps={() => ({
        style: {
          alignItems: "stretch",
        },
      })}
      getTableProps={() => ({
        onScroll: handleTransactionsTableScroll,
      })}
    />
  )
}, arePropsEqual)

export const Transactions = ({
  transactions,
  getTransactions,
  approveTransaction,
  editTransaction,
  clearTransactions,
  deleteSeatsTransaction,
  cancelOfferTransaction,
  currentEvent,
  token,
  match: { params },
}) => {
  const [user, setUser] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [requestedSearchQuery, setRequestedSearchQuery] = useState("");
  const [searchByInputNumber, setSearchByInputNumber] = useState(false);
  const [searchPaymentStatus, setSearchPaymentStatus] = useState(null);
  const [searchPaymentType, setSearchPaymentType] = useState(null);
  const [sortingType, setSortingType] = useState(sortTypes.dateDescending);
  const [isActionLoading, setActionLoading] = useState(false);
  const [columnsList, setColumnsList] = useState([]);
  const [checkedColumns, setCheckedColumns] = useState([]);
  const [scrollThumbWidth, setScrollThumbWidth] = useState(0);

  const [currentPageNumber, setCurrentPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(100);

  const { API_URL, PUBLIC_URL } = getCustomURLs();

  const event_id = params.event_id || params.id;
  const {
    transactionsList,
    transactionsTotal,
    firstTransactionsLoading,
    transactionsLoading,
  } =
    transactions;

  const [showMenuActions, hideMenuActions] = useShowActionsMenu();

  const [page, handleTableScroll, resetPage] = useLazyLoadOnTableScroll(
    pageSize,
    transactionsTotal
  );

  const handleCurrentPageChanged = (value) => {
    setCurrentPageNumber(value);
  };

  const handlePageSizeChanged = (value) => {
    setPageSize(value);
    setCurrentPageNumber(0);
  };

  const handleTransactionsTableScroll = (e) => {
    handleTableScroll(e);
    hideMenuActions();
  };

  const changeSortingType = (column) => {
    if (column === 'status') {
      setSortingType((curr) => (curr === sortTypes.statusAscending ? sortTypes.statusDescending : sortTypes.statusAscending));
    }
    if (column === 'creation date') {
      setSortingType((curr) => (curr === sortTypes.dateAscending ? sortTypes.dateDescending : sortTypes.dateAscending));
    }
    if (column === 'payment date') {
      setSortingType((curr) => (curr === sortTypes.paymentDateAscending ? sortTypes.paymentDateDescending : sortTypes.paymentDateAscending));
    }
    
    resetPage();
    clearTransactions();
  };

  const handleDebonceFn = (value) => {
    setRequestedSearchQuery(value.trim());

    setCurrentPageNumber(0);
  };

  const debounceFn = useCallback(debounce(handleDebonceFn, 1500), []);

  const changeSearchInputHandler = (e) => {
    resetPage();
    setSearchQuery(e.target.value);
    debounceFn(e.target.value);
  };

  const changeSearchByInputNumberHandler = () => {
    resetPage();
    setSearchByInputNumber(v => !v);
    setCurrentPageNumber(0);
  };

  const changeSearchPaymentStatusHandler = (item) => {
    resetPage();
    setSearchPaymentStatus(item);
    setCurrentPageNumber(0);
  };

  const changeSearchPaymentTypeHandler = (item) => {
    resetPage();
    setSearchPaymentType(item);
    setCurrentPageNumber(0);
  };

  const loadUser = () => {
    const currentUser = JSON.parse(localStorage.getItem("user"));
    setUser(currentUser);
  };

  const getEventTransactions = (userToken) => {
    getTransactions(
      userToken,
      requestedSearchQuery,
      searchByInputNumber,
      searchPaymentStatus == null ? [] : [searchPaymentStatus.value],
      event_id,
      "",
      currentPageNumber,
      pageSize,
      sortingType,
      !searchPaymentType ? [] : [searchPaymentType.value],
    );
  }

  const showApproveTransactionModal = (transaction) => {
    MySwal.fire({
      html: (
        <ApproveTransactionModal
          token={user.token}
          transactionId={transaction.id}
          approveTransaction={approveTransaction}
          getEventTransactions={getEventTransactions}
        />
      ),
      showConfirmButton: false,
      customClass: "approve-user-modal",
    });
  };

  const showEditTransactionModal = (transaction) => {
    MySwal.fire({
      title: "Edit Information",
      html: (
        <EditTransactionModal
          token={user.token}
          editTransaction={editTransaction}
          deleteSeatsTransaction={deleteSeatsTransaction}
          transaction={transaction}
          getEventTransactions={getEventTransactions}
        />
      ),
      showConfirmButton: false,
      customClass: "edit-user-modal",
    });
  };

  const showRefundTicketsModal = (transaction) => {
    MySwal.fire({
      title: "Refund tickets",
      html:
        <RefundTicketsModal
          token={user.token}
          transaction={transaction}
          setActionLoading={setActionLoading}
          getEventTransactions={getEventTransactions}
        />,
      showConfirmButton: false,
      customClass: "refund-tickets-modal",
    });
  };

  const resendTicketsHandler = (transaction) => {
    axios
      .post(`/ManageTransactions/ResendTicket/${transaction.id}`, null, {
        headers: getHeaders(user.token),
      })
      .then((response) => {
        if (response.status === 200) {
          if (response.data) {
            toast.success("Tickets successfully resent");
          } else {
            toast.error("Tickets not sent");
          }
          return;
        }

        throw new Error(response.data.errorMessage);
      })
      .catch((err) => {
        toast.error("Tickets not sent");
      });
  };

  const resendInvoiceHandler = (transaction) => {
    axios
      .post(
        `/ManageTransactions/ResendInvoice/${transaction.id}`,
        null,
        {
          headers: getHeaders(user.token),
        }
      )
      .then((response) => {
        if (response.status === 200) {
          toast.success("Invoice successfully resent");
          return;
        }

        throw new Error(response.data.errorMessage);
      })
      .catch((err) => {
        toast.error("Invoice not sent");
      });
  };

  const showLinkCopied = (link) => {
    ClipboardJS.copy(link);

    toast.dismiss(257);
    toast.success("Link was copied to clipboard.", {
      toastId: 257,
    });
  };

  const copyOfferLinkHandler = (transaction) => {
    showLinkCopied(
      `${PUBLIC_URL}/offers/${transaction.id}`
    );
  };
  const copyInvoiceLinkHandler = (transaction) => {
    showLinkCopied(
      `${API_URL}/Transaction/Invoice/${transaction.invoicePDFGuid}`
    );
  };

  const copyTicketLinkHandler = (transaction) => {
    showLinkCopied(
      `${API_URL}/Transaction/Ticket/${transaction.ticketResultGuid}`
    );
  };

  const cancelOfferHandler = (transaction) => {
    MySwal.fire({
      title: "Are you sure you want to cancel offer?",
      text: "It’s impossible to take step back then",
      showCancelButton: true,
      cancelButtonText: "Cancel",
      confirmButtonText: "Confirm",
      reverseButtons: true,
      customClass: "confirm-cancel-offer-modal",
    }).then((result) => {
      if (result.value) {
        setActionLoading(true)
        cancelOfferTransaction(user.token, transaction)
          .then(() => {
            setActionLoading(false)
            getEventTransactions(user.token);
          })
          .catch(() => {
            setActionLoading(false)
          });
      }
    });
  };

  const downloadReportHandler = () => {
    const { id, name } = currentEvent;
    const fileName =
      name.toLowerCase().replaceAll(" ", "_") + "_transactions_report";

    MySwal.fire({
      html: <PleaseWaitModal message='Downloading file…' />,
      customClass: 'please-wait-modal',
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
    });

    axios({
      method: "GET",
      url: API_URL + `/Events/${id}/ByPaidTransactionsReport`,
      responseType: "arraybuffer",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    })
      .then((response) => {
        loadFile(
          response.data,
          `${fileName}_${moment().format("MM_DD_HH_mm")}.csv`
        );
      })
      .catch((error) => console.log(error))
      .finally(() => {
        MySwal.close();
      });
  };

  useEffect(() => {
    if (user.token) {
      getEventTransactions(user.token);
    }
  }, [
      user,
      requestedSearchQuery,
      searchPaymentStatus,
      sortingType,
      searchByInputNumber,
      currentPageNumber,
      pageSize,
      searchPaymentType,
    ]
  );

  useEffect(() => {
    clearTransactions();
    loadUser();
  }, []);

  const getLabelStyle = (state, rowInfo, column) => ({
    style: {
      marginTop: "6px",
    },
  });

  const getFlexStyle = () => ({
    style: {
      padding: "18px 5px 15px",
      display: "flex",
      alignItems: "start",
      marginRight: 0,
      marginLeft: "auto",
    },
  });

  const getStatusStyle = () => ({
    style: {
      marginTop: "3px",
      paddingTop: "20px",
      display: "flex",
      alignItems: "start",
    },
  });
  const getCorrectedTextStyle = () => ({
    style: {
      marginTop: "3px",
    },
  });

  const columns = [
    {
      Header: "Name",
      accessor: "fullName",
      resizable: false,
      getProps: getLabelStyle,
      width: 220,
      Cell: ({ row: { _original: { fullName } } }) => (
        <span title={fullName}>{fullName}</span>
      ),
      id: "Name",
    },
    {
      Header: "Email",
      accessor: "buyerEmail",
      resizable: false,
      getProps: getCorrectedTextStyle,
      width: 220,
      Cell: ({ row: { _original: { buyerEmail, id } } }) => (
        <span kew={id} title={buyerEmail}>{buyerEmail}</span>
      ),
      id: "Email",
    },
    {
      Header: "Ticket Type",
      accessor: "seatsDescription",
      resizable: false,
      width: 160,
      getProps: getLabelStyle,
      Cell: ({ row: { _original: { seats } } }) => (
        seats.map(({ ticketTemplate: { ticketName, id }, refunded, isDeleted }, idx) => {
          if (!refunded && !isDeleted) {
            return (
              <div key={id + idx} className="td-content" title={ticketName}>
                {ticketName}
              </div>
            )
          }

          return null;
        })
      ),
      id: "Ticket Type",
    },
    {
      Header: "Seats",
      accessor: "seatsDescription",
      resizable: false,
      width: 130,
      getProps: getLabelStyle,
      Cell: ({ row: { _original: { seats } } }) => (
        seats.map(({ area, side, row, seat, refunded, isActual, id }) => {
          if (area && side && row && seat && !refunded && isActual) {
            return (
              <div key={id}>{`${area}-${side}-${row}-${seat}`}</div>
            );
          };
          return null
        })
      ),
      id: "Seats",
    },
    {
      Header: () => (
        <div className="payment-status" onClick={() => changeSortingType('status')}>
          <span>Status</span>
          <Icon name="arrow-up-down" />
        </div>
      ),
      resizable: false,
      width: 135,
      getProps: getStatusStyle,
      Cell: ({
        row: {
          _original: {
            transactionErrorCode,
            transactionErrorMessage,
            paymentStatus,
            isOfferCanceled,
          },
        },
      }) => {
        const description =
          paymentStatus === 2
            ? transactionErrorMessage
              ? `${transactionErrorCode}: ${transactionErrorMessage}`
              : ""
            : "";

        const currentStatus = isOfferCanceled ? 3 : paymentStatus;

        return (
          <StatusLabel statusValue={currentStatus} description={description} />
        );
      },
      id: "Status",
    },
    {
      Header: "Payment Type",
      resizable: false,
      width: 130,
      getProps: getLabelStyle,
      Cell: ({ row: { _original: transaction } }) => <>{getPaymentTypeLabel(transaction.paymentType)}</>,
      id: "Payment Type",
    },
    {
      Header: "Transaction ID",
      accessor: "id",
      resizable: false,
      width: 340,
      getProps: getLabelStyle,
      Cell: ({ row: { _original: { id } } }) => (
        <span title={id}>{id}</span>
      ),
      id: "Transaction ID",
    },
    {
      Header: "Order ID",
      resizable: false,
      width: 340,
      getProps: getLabelStyle,
      Cell: ({ row: { _original } }) => (
        !_original.invoice
          ? <></>
          : <span title={_original.invoice.id}>{_original.invoice.id}</span>
      ),
      id: "Order ID"
    },
    {
      Header: () => {
        return <div className="payment-status" onClick={() => changeSortingType('creation date')}>
          <span>Creation Date</span>
          <Icon name="arrow-up-down" />
        </div>
      },
      resizable: false,
      width: 160,
      getProps: getLabelStyle,
      Cell: ({ row: { _original: transaction } }) => {
        const date = new Date(transaction.createdAtUtc)
        return <>{
          date.toLocaleDateString()
        } {date.toLocaleTimeString()}</>
      },
      id: "Creation Date"
    },
    {
      Header: () => {
        return <div className="payment-status" onClick={() => changeSortingType('payment date')}>
          <span>Payment Date</span>
          <Icon name="arrow-up-down" />
        </div>

      },
      resizable: false,
      width: 160,
      getProps: getLabelStyle,
      Cell: ({ row: { _original: transaction } }) => {
        const date = new Date(transaction.paymentDate)

        if (date.getTime() < new Date(2000, 1, 1, 0, 0, 0, 0).getTime()) {
          return <span>N/A</span>
        }

        return <>{
          date.toLocaleDateString()
        } {date.toLocaleTimeString()}</>
      },
      id: "Payment Date"
    },
    {
      Header: "Invoice",
      resizable: false,
      getProps: getLabelStyle,
      width: 130,
      Cell: ({ row: { _original: transaction } }) => <>{transaction.invoice && transaction.invoice.number}</>,
      id: "Invoice",
    },
    {
      Header: "Additional Info",
      resizable: false,
      getProps: getLabelStyle,
      width: 350,
      Cell: ({ row: { _original: transaction } }) => {
        return <Fragment key={transaction.id}>
          {transaction.paymentType === paymentTypes.delayedPayment
            ? <div id="table-info" className="event-transactions__table-info">
                <div>Company name: {transaction.companyName} </div>
                <div>Company VAT: {transaction.companyVatNumber} </div>
                <div>Invoice amount: {transaction.amount} {transaction.currency || 'RSD'}</div>
              </div>
            : <ol>{transaction.info.map(i => <li key={i.id} style={{ margin: '2px' }}>{i.description}</li>)}</ol>
          }
        </Fragment>
      },
      id: "Additional Info",
    },
    {
      Cell: ({ row: { _original: transaction } }) => (
        <TransactionActions
          transaction={transaction}
          showApproveTransactionModal={showApproveTransactionModal}
          showEditTransactionModal={showEditTransactionModal}
          showRefundTicketsModal={showRefundTicketsModal}
          resendTicketsHandler={resendTicketsHandler}
          resendInvoiceHandler={resendInvoiceHandler}
          copyOfferLinkHandler={copyOfferLinkHandler}
          copyInvoiceLinkHandler={copyInvoiceLinkHandler}
          copyTicketLinkHandler={copyTicketLinkHandler}
          cancelOfferHandler={cancelOfferHandler}
          allowMaximize={(transaction.seats.length > 2) || (transaction.paymentType === paymentTypes.delayedPayment)}
        />
      ),
      getProps: getFlexStyle,
      width: 120,
      fixed: "right",
      id: null,
    },
  ];

  const ALL = 'Select all';

  const checkboxesList = [ALL].concat(columns.filter(c => c.id).map(c => c.id));

  const toggleColumn = (col) => {
    let newCheckedColumns = [];

    if (col === ALL) {
      if (checkedColumns.includes(col)) {
        newCheckedColumns = checkboxesList.slice(1, 2);
      } else {
        newCheckedColumns = [...checkboxesList];
      }
    } else if (checkedColumns.includes(col)) {
      newCheckedColumns = checkedColumns.filter(c => c !== col && c !== ALL);
    } else {
      const colArray = [...checkedColumns, col];
      if (colArray.length === checkboxesList.length - 1) {
        newCheckedColumns = [...checkboxesList]
      } else {
        newCheckedColumns = columns.filter(c => colArray.includes(c.id)).map(c => c.id);
      }
    };

    setCheckedColumns(newCheckedColumns);

    ls.set("trColumns", newCheckedColumns);
  };

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      height: "46px",
      "min-height": "46px",
      cursor: "pointer",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#ABABAB",
    }),
  };

  let emptyStateText = "There are no transactions";

  if (transactionsTotal === 0 && requestedSearchQuery) {
    emptyStateText = "Sorry, no results were found";
  };

  useEffect(() => {
    const selectedColumns = columns.filter(c => checkedColumns.includes(c.id));
    setColumnsList([...selectedColumns, columns.at(-1)]);
  }, [checkedColumns]);

  useEffect(() => {
    if (ls.get("trColumns")) {
      setCheckedColumns(ls.get("trColumns"));
    } else {
      setCheckedColumns(checkboxesList);
    }
  }, []);

  useEffect(() => {
    setTimeout(() => {
      const list = document.getElementsByClassName('rt-table');

      if (list && list.length > 0) {
        const table = list[0];
  
        setScrollThumbWidth(table.offsetWidth - table.clientWidth);
      }
    }, 0)
  }, [transactionsList]);

  return (
    <div className="new-event__transactions event-transactions">
      <div className="searchPanel">
        <Select
          isClearable
          options={paymentStatuses}
          placeholder="Select status"
          name="searchPaymentStatus"
          classNamePrefix="custom-select"
          value={searchPaymentStatus}
          onChange={changeSearchPaymentStatusHandler}
          className="seasonpass__select searchPanel__select"
          styles={customSelectStyles}
          theme={(theme) => ({
            ...theme,
            colors: {
              ...theme.colors,
              primary: "#6071B5",
              primary25: "#F2F4FE",
              primary50: "#F2F4FE",
              primary75: "#F2F4FE",
            },
          })}
        />

        <Select
          isClearable
          options={paymentOptions}
          placeholder="Select payment type"
          name="searchPaymentType"
          classNamePrefix="custom-select"
          value={searchPaymentType}
          onChange={changeSearchPaymentTypeHandler}
          className="seasonpass__select searchPanel__select"
          styles={customSelectStyles}
          theme={(theme) => ({
            ...theme,
            colors: {
              ...theme.colors,
              primary: "#6071B5",
              primary25: "#F2F4FE",
              primary50: "#F2F4FE",
              primary75: "#F2F4FE",
            },
          })}
        />

        <input
          className="searchPanel__search input-behavior search-icon"
          type="text"
          placeholder="Search"
          value={searchQuery}
          onChange={changeSearchInputHandler}
        />

        <CheckBox checked={searchByInputNumber}
          id='searchByInputNumber'
          name="searchByInputNumber"
          onChange={changeSearchByInputNumberHandler}>
          <span className="input-behavior">
            Search by invoice
          </span>
        </CheckBox>

        <button
          className='btn-primary'
          onClick={downloadReportHandler}
        >
          Download Report
        </button>
      </div>

      {(firstTransactionsLoading || transactionsLoading || isActionLoading) ? (
        <div className="event-transactions__loader">
          <NewLoader />
        </div>
      ) : transactionsList.length ? (
        <>
          <div
            className="event-transactions__table-settings-wrapper"
            style={{
              right: 40 + scrollThumbWidth + 'px'
            }}
          >
            <TabelSettings
              checkboxesList={checkboxesList}
              checkedColumns={checkedColumns}
              toggleColumn={toggleColumn}
            />
          </div>
          <Table
            columns={columnsList}
            currentPageNumber={currentPageNumber}
            pageSize={pageSize}
            totalTransactions={transactionsTotal}
            transactionsList={transactionsList}
            handleCurrentPageChanged={handleCurrentPageChanged}
            handlePageSizeChanged={handlePageSizeChanged}
            handleTransactionsTableScroll={handleTransactionsTableScroll}
          />
        </>
      ) : (
        <div className="events__empty-state">
          <PageTitle>{emptyStateText}</PageTitle>
          <img src="/images/img-empty-events.svg" alt="Empty transactions" />
        </div>
      )}
    </div>
  );
};

const mapStateToProps = ({ auth, transactions, events }) => ({
  auth,
  token: auth.user.token,
  transactions,
  currentEvent: events.currentEvent,
});

const mapDispatchToProps = (dispatch) => ({
  getTransactions: (
    userToken,
    searchQuery,
    searchByInputNumber,
    paymentStatuses,
    eventId,
    transactionStatus,
    page,
    pageSize,
    sortingType,
    paymentType
  ) =>
    dispatch(
      getTransactions(
        userToken,
        searchQuery,
        searchByInputNumber,
        paymentStatuses,
        eventId,
        transactionStatus,
        page,
        pageSize,
        sortingType,
        paymentType
      )
    ),
  approveTransaction: (userToken, id, makeFiscalization, onConfirm) =>
    dispatch(approveTransaction(userToken, id, makeFiscalization, onConfirm)),
  editTransaction: (userToken, transaction) =>
    dispatch(editTransaction(userToken, transaction)),
  clearTransactions: () => dispatch(clearTransactions()),
  deleteSeatsTransaction: (userToken, transaction, seatIds) =>
    dispatch(deleteSeatsTransaction(userToken, transaction, seatIds)),
  cancelOfferTransaction: (userToken, transaction) =>
    dispatch(cancelOfferTransaction(userToken, transaction)),
  getEventSellStats: (eventId, token) =>
    dispatch(getEventSellStats(eventId, token)),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(Transactions)
);
